import { useEffect, useRef, useState } from 'react'

import {
  CurrentPlayerPokerViewDocument,
  useCurrentPlayerPokerStackUpdateMutation,
} from '../../../__generated__/hooks'
import { useWakeLock } from '../../../hooks/useWakeLock'
import type { PayJump } from '../../../util/poker'
import BubbleBanner from '../components/BubbleBanner'
import Clock from '../components/Clock'
import ClockFullscreen from '../components/ClockFullscreen'
import PayoutList from '../components/PayoutList'
import SeatCard from '../components/SeatCard'
import StackEditSheet from '../components/StackEditSheet'
import StandingsBoard from '../components/StandingsBoard'
import TableGrid from '../components/TableGrid'
import TableMoveBanner from '../components/TableMoveBanner'
import type { PokerView } from '../hooks/usePokerView'

const toDate = (s: string | null | undefined): Date | null =>
  s ? new Date(s) : null

/**
 * The dashboard for an active (still-in) player — Running or Paused.
 * Composes the clock + seat hero + bubble/intel + table + standings +
 * payouts.
 *
 * Owns:
 *  - the stack-edit mutation + bottom sheet
 *  - the wake-lock acquisition (no screen sleep while on this surface)
 *
 * The pause banner is rendered inline above the clock — `usePokerView`
 * sets `isPaused` and the dispatcher routes Paused state HERE (not to a
 * separate view), since the layout is identical and only one tiny banner
 * differs.
 */
const ActiveView = ({
  view,
  isPaused,
  myBB,
  avgBB,
  myPosition,
  payJump,
  playerKey,
}: {
  view: PokerView
  isPaused: boolean
  myBB: number | null
  avgBB: number | null
  myPosition: number | null
  payJump: PayJump | null
  playerKey: string
}) => {
  useWakeLock(true)

  const [stackOpen, setStackOpen] = useState(false)
  const [stackError, setStackError] = useState<string | null>(null)
  const [fullscreenClock, setFullscreenClock] = useState(false)
  // The mutation returns the seat as PolfEventPokerPlayerSeat — the same type
  // the view reads — so its result merges into the watched seat and the hero
  // updates on the tap. The refetch is for the numbers the seat alone can't
  // give: average stack, BB counts and the player's row in the standings.
  //
  // Object form on purpose here, unlike the Score page: it issues ONE
  // throwaway fetch and lets the broadcast fan out, whereas the name form
  // would reobserve every mounted copy of this query — and both the shell and
  // this page hold one, so a save would cost two requests on the surface with
  // the tightest poll in the app.
  const [updateStack, { loading: savingStack }] =
    useCurrentPlayerPokerStackUpdateMutation({
      refetchQueries: [{ query: CurrentPlayerPokerViewDocument }],
      awaitRefetchQueries: true,
    })

  // PayoutList highlight: when player is ITM, mark their probable finish.
  // For bubble or no-payjump, no highlight.
  const highlightPosition =
    payJump?.kind === 'secured' ? payJump.positionIfNow : null

  // Table-move detection. We track the last-seen seat in a ref (no
  // re-render on update) and surface a banner whenever the polled view
  // shows a different table or seat number. First mount seeds the ref —
  // we never show a "moved" notice for the initial render. Dismissing
  // clears `moveNotice` but keeps the ref pinned to the new values, so
  // the banner only re-appears on a SUBSEQUENT real move.
  const lastSeenSeatRef = useRef<{
    tableNumber: number
    seatNumber: number
  } | null>(null)
  const [moveNotice, setMoveNotice] = useState<{
    fromTable: number
    fromSeat: number
    toTable: number
    toSeat: number
  } | null>(null)
  const currentTable = view.seat?.tableNumber ?? null
  const currentSeat = view.seat?.seatNumber ?? null
  useEffect(() => {
    if (currentTable == null || currentSeat == null) return
    const prev = lastSeenSeatRef.current
    if (prev === null) {
      lastSeenSeatRef.current = {
        tableNumber: currentTable,
        seatNumber: currentSeat,
      }
      return
    }
    if (prev.tableNumber !== currentTable || prev.seatNumber !== currentSeat) {
      setMoveNotice({
        fromTable: prev.tableNumber,
        fromSeat: prev.seatNumber,
        toTable: currentTable,
        toSeat: currentSeat,
      })
      lastSeenSeatRef.current = {
        tableNumber: currentTable,
        seatNumber: currentSeat,
      }
    }
  }, [currentTable, currentSeat])

  return (
    <section className="pb-4">
      {isPaused ? (
        <div className="mx-4 my-3 px-4 py-2 rounded-lg bg-warn/20 text-warn text-sm font-bold text-center">
          Pause
        </div>
      ) : null}

      {moveNotice ? (
        <TableMoveBanner
          fromTable={moveNotice.fromTable}
          fromSeat={moveNotice.fromSeat}
          toTable={moveNotice.toTable}
          toSeat={moveNotice.toSeat}
          onDismiss={() => setMoveNotice(null)}
        />
      ) : null}

      <BubbleBanner payJump={payJump} />

      {view.currentLevel ? (
        <Clock
          level={view.currentLevel}
          nextLevel={view.nextLevel ?? null}
          inBreak={view.currentInBreak ?? false}
          startedAt={toDate(view.currentLevelStartedAt)}
          pausedAt={toDate(view.currentLevelPausedAt)}
          levelIndex={view.currentLevelIndex ?? 0}
          status={view.status!}
          playersLeft={view.playersLeft}
          playersTotal={view.playersTotal}
          averageStack={view.averageStack}
          onExpand={() => setFullscreenClock(true)}
        />
      ) : null}

      {view.currentLevel ? (
        <ClockFullscreen
          open={fullscreenClock}
          level={view.currentLevel}
          nextLevel={view.nextLevel ?? null}
          inBreak={view.currentInBreak ?? false}
          startedAt={toDate(view.currentLevelStartedAt)}
          pausedAt={toDate(view.currentLevelPausedAt)}
          levelIndex={view.currentLevelIndex ?? 0}
          status={view.status!}
          playersLeft={view.playersLeft}
          playersTotal={view.playersTotal}
          averageStack={view.averageStack}
          myBB={myBB}
          avgBB={avgBB}
          myPosition={myPosition}
          moveNotice={moveNotice}
          onDismissMove={() => setMoveNotice(null)}
          onClose={() => setFullscreenClock(false)}
        />
      ) : null}

      {view.seat ? (
        <SeatCard
          seat={view.seat}
          myBB={myBB}
          avgBB={avgBB}
          payJump={payJump}
          allowEdit={view.allowPlayerStackUpdate}
          onEditStack={() => {
            setStackError(null)
            setStackOpen(true)
          }}
        />
      ) : null}

      {view.seat ? (
        <TableGrid
          tableNumber={view.seat.tableNumber}
          seats={view.tableSeats}
          myCurrentStack={view.seat.currentStack}
        />
      ) : null}

      <StandingsBoard
        active={view.activeStandings}
        eliminated={view.eliminations}
        yourSeatId={view.seat?.id}
        isFinished={false}
        playerKey={playerKey}
      />

      {view.usePayouts && view.payouts.length > 0 ? (
        <PayoutList
          payouts={view.payouts}
          prizePool={view.prizePool}
          highlightPosition={highlightPosition}
        />
      ) : null}

      {stackError ? (
        <div className="mx-4 -mt-2 mb-2 text-sm text-flag-dark text-center">
          {stackError}
        </div>
      ) : null}

      {view.seat ? (
        <StackEditSheet
          open={stackOpen}
          initial={view.seat.currentStack}
          saving={savingStack}
          onClose={() => setStackOpen(false)}
          onSave={async (next) => {
            try {
              await updateStack({ variables: { currentStack: next } })
              setStackOpen(false)
            } catch (err) {
              setStackError(
                (err as { message?: string })?.message ??
                  'Kunne ikke oppdatere stack'
              )
            }
          }}
        />
      ) : null}
    </section>
  )
}

export default ActiveView
